import { ReactElement } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import makeStyles from '@material-ui/core/styles/makeStyles';
import useTheme from '@material-ui/core/styles/useTheme';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import Grid from '@material-ui/core/Grid';
import Link from '@material-ui/core/Link';
import Typography from '@material-ui/core/Typography';
import Button from '@material-ui/core/Button';
import {
  BPC_NEW_PORTAL_LINK,
  BUCKAROO_PORTAL_LINK,
  BEL_SUPPORT_EMAIL,
  LUX_SUPPORT_EMAIL,
} from '@/constants/migration';
import HeaderLanding from '@/components/Layouts/HeaderLanding';

const useStyles = makeStyles((theme) => ({
  wrapper: {
    gap: theme.spacing(1),
    marginInline: 0,
    border: 'none',
    marginTop: '50px',
    flexDirection: 'column-reverse',
    [theme.breakpoints.up('sm')]: {
      maxWidth: 820,
      gap: theme.spacing(1),
      marginInline: 'auto',
      border: `1px solid ${theme.palette.common.border}`,
      flexDirection: 'row',
      flexWrap: 'nowrap',
    },
  },
  countryPanel: {
    flex: 1,
    background: '#F9F7F5',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    padding: '40px 25px',
  },
  countrySection: {
    padding: theme.spacing(2, 0),
    '&:first-child': {
      paddingTop: 0,
    },
    '&:last-child': {
      paddingBottom: 0,
    },
  },
  countryHeading: {
    textTransform: 'uppercase',
    fontWeight: 700,
    letterSpacing: 1,
    marginBottom: theme.spacing(1.5),
  },
  countryButton: {
    padding: theme.spacing(1.5, 2),
    fontSize: theme.typography.pxToRem(16),
    lineHeight: theme.typography.pxToRem(16),
    fontWeight: 400,
    marginBottom: theme.spacing(1.5),
  },
  countryDivider: {
    width: '100%',
    height: 1,
    backgroundColor: '#d9d9d9',
    margin: theme.spacing(2, 0),
  },
  title: {
    marginBottom: 36,
    lineHeight: theme.typography.pxToRem(36),
    fontWeight: 400,
    [theme.breakpoints.up('sm')]: {
      marginBottom: theme.spacing(3),
    },
  },
  subtitle: {
    lineHeight: theme.typography.pxToRem(22),
    paddingRight: 20,
    paddingBottom: 36,
  },
  subtitle1: {
    lineHeight: theme.typography.pxToRem(22),
    paddingBottom: 20,
    paddingRight: 20,
  },
  boxLeft: {
    padding: '40px 20px 44px 24px',
    flex: 1,
  },
}));

function MerchantPortalTransitionPage(): ReactElement {
  const { t } = useTranslation();
  const classes = useStyles();
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('sm'));

  return (
    <>
      <>
        <HeaderLanding />
      </>
      <Grid container justifyContent="center" className={classes.wrapper}>
        <Grid item xs={12} sm={6} className={classes.boxLeft}>
          <Typography component="h1" variant="h1" color="secondary" className={classes.title}>
            <Trans i18nKey="portal_transition.title" components={{ bold: <b /> }} />
          </Typography>
          <Typography className={classes.subtitle}>
            <Trans i18nKey="portal_transition.message" />
          </Typography>
          <Typography className={classes.subtitle1}>
            <Trans i18nKey="portal_transition.button_message" />
          </Typography>
        </Grid>
        <Grid item xs={12} sm={6} className={classes.countryPanel}>
          <div className={classes.countrySection}>
            <Typography className={classes.countryHeading} variant="body2">
              {t('portal_transition.bel.heading')}
            </Typography>
            <Button
              variant="contained"
              color="primary"
              disableElevation
              className={classes.countryButton}
              href={BPC_NEW_PORTAL_LINK}
              target="_blank"
              rel="noopener"
              fullWidth={!isDesktop}
            >
              {t('portal_transition.bel.button')}
            </Button>
            <Typography>
              <Trans
                i18nKey="portal_transition.bel.help_text"
                components={{
                  email: <Link href={`mailto:${BEL_SUPPORT_EMAIL}`} />,
                }}
              />
            </Typography>
          </div>
          <div className={classes.countryDivider} />
          <div className={classes.countrySection}>
            <Typography className={classes.countryHeading} variant="body2">
              {t('portal_transition.lux.heading')}
            </Typography>
            <Button
              variant="contained"
              color="primary"
              disableElevation
              className={classes.countryButton}
              href={BUCKAROO_PORTAL_LINK}
              target="_blank"
              rel="noopener"
              fullWidth={!isDesktop}
            >
              {t('portal_transition.lux.button')}
            </Button>
            <Typography>
              <Trans
                i18nKey="portal_transition.lux.help_text"
                components={{
                  email: <Link href={`mailto:${LUX_SUPPORT_EMAIL}`} />,
                }}
              />
            </Typography>
          </div>
        </Grid>
      </Grid>
    </>
  );
}

export default MerchantPortalTransitionPage;
